/**
 * The connection dialog's motion values (Option C, C1b, plan §3.2 "Timing"), read from the motion-token subset installed
 * once in `src/index.css` (transitions-dev `_root.css`), so a tweak there applies here. The fallbacks are the installed
 * values. Each value is chosen by what the motion does (transitions-polish: match on usage, never on the nearest number):
 *
 * | Value | Token | Usage |
 * |---|---|---|
 * | `shared` 300 ms | `--resize-dur` | cards ↔ rail items: they change size as well as position (card resize, D18) |
 * | `fast` 250 ms | `--duration-fast` | the pane entering (page slide, recipe 08), the narrow page slide, the rail highlight moving (tabs sliding, recipe 16), incoming chrome, the pane swap on an engine switch (in-place swap) |
 * | `stagger` 40 ms | `--duration-stagger` | sequencing: the pane and the start view's chrome start one beat after the shared items |
 * | `quick` 150 ms | `--duration-quick` | reduced motion's opacity cross-fade (a swap, like text swap) |
 * | `partOut` 90 ms | — | a card's blurb leaving: no token has this usage, so it stays a literal |
 * | `distance` 8 px | `--distance-base` | page slide |
 * | `paneBlur` 3 px | `--blur-medium` | page slide (off: `PANE_BLUR`) |
 * | `swapBlur` 2 px | `--blur-small` | the pane swap, an icon-swap-like cross-blur (off: `PANE_BLUR`) |
 * | `ease` | `--ease-smooth-out` | every open / close / slide / resize / position change |
 * | `swapEase` | `ease-in-out` | the two in-place swaps (the pane on an engine switch, reduced motion's cross-fade) |
 *
 * `swapEase` is the transitions-dev `--ease-in-out` value written as its keyword: the token itself is not installed
 * because Tailwind's theme owns `--ease-in-out` (its `ease-in-out` utility reads it, with another curve).
 * `PANE_BLUR` is the plan's performance switch, and it is OFF: the Linux / xvfb trace (C1b NOTES) showed three 50-ms
 * frames in every forward flight with the pane's 3-px blur and none without it (60 fps throughout), so the pane slides
 * and swaps with opacity and transform only. The blur tokens stay installed for the day a trace allows them again.
 */
export type MotionTokens = {
  shared: number;
  fast: number;
  stagger: number;
  quick: number;
  partOut: number;
  distance: number;
  paneBlur: number;
  swapBlur: number;
  ease: string;
  swapEase: string;
};

export const PANE_BLUR = false;

export const DEFAULT_MOTION_TOKENS: MotionTokens = {
  shared: 300,
  fast: 250,
  stagger: 40,
  quick: 150,
  partOut: 90,
  distance: 8,
  paneBlur: 3,
  swapBlur: 2,
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
  swapEase: 'ease-in-out',
};

/** `'300ms'` / `'0.3s'` → 300; anything else → `fallback`. */
export function parseDuration(value: string, fallback: number): number {
  const match = /^\s*(-?[\d.]+)\s*(ms|s)\s*$/u.exec(value);
  if (!match) return fallback;
  const amount = Number.parseFloat(match[1]);
  if (!Number.isFinite(amount) || amount < 0) return fallback;
  return match[2] === 's' ? amount * 1000 : amount;
}

/** `'8px'` → 8; anything else → `fallback`. */
export function parseLength(value: string, fallback: number): number {
  const match = /^\s*([\d.]+)\s*px\s*$/u.exec(value);
  const amount = match ? Number.parseFloat(match[1]) : Number.NaN;
  return Number.isFinite(amount) ? amount : fallback;
}

type TokenSource = Pick<CSSStyleDeclaration, 'getPropertyValue'>;

/** The tokens, with the pane blurs zeroed while the performance switch is off. */
export function readMotionTokens(source: TokenSource | null, paneBlur: boolean = PANE_BLUR): MotionTokens {
  const tokens = readTokenValues(source);
  return paneBlur ? tokens : { ...tokens, paneBlur: 0, swapBlur: 0 };
}

function readTokenValues(source: TokenSource | null): MotionTokens {
  const d = DEFAULT_MOTION_TOKENS;
  if (!source) return d;
  const read = (name: string) => source.getPropertyValue(name) ?? '';
  const ease = read('--ease-smooth-out').trim();
  return {
    shared: parseDuration(read('--resize-dur'), d.shared),
    fast: parseDuration(read('--duration-fast'), d.fast),
    stagger: parseDuration(read('--duration-stagger'), d.stagger),
    quick: parseDuration(read('--duration-quick'), d.quick),
    partOut: d.partOut,
    distance: parseLength(read('--distance-base'), d.distance),
    paneBlur: parseLength(read('--blur-medium'), d.paneBlur),
    swapBlur: parseLength(read('--blur-small'), d.swapBlur),
    ease: ease || d.ease,
    swapEase: d.swapEase,
  };
}
